import { translate } from '../../i18n/index.js';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link as RouterLink, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import type React from 'react';
import { createPortal } from 'react-dom';
import { AtomFitLogo, Tag } from '../design/AtomFitUI.js';
import { useAuth } from '../../hooks/useAuth.js';
import { adminApi } from '../../api/admin.api.js';
import { LanguageSwitcher } from '../common/LanguageSwitcher.js';

interface AdminLayoutProps {
  children: React.ReactNode;
  title: string;
  eyebrow: string;
  headerActions?: React.ReactNode;
}

interface AdminChromeState {
  title: string;
  eyebrow: string;
}

interface AdminChromeContextValue {
  setChrome: React.Dispatch<React.SetStateAction<AdminChromeState>>;
  actionsTarget: HTMLDivElement | null;
}

const AdminChromeContext = createContext<AdminChromeContextValue | null>(null);

interface AdminNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  end?: boolean;
  badge?: string;
  platformOnly?: boolean;
  ownerOnly?: boolean;
  operatorAllowed?: boolean;
}

const navGroups: Array<{ group: string | null; items: AdminNavItem[] }> = [
  {
    group: null,
    items: [
      {
        label: translate('components.layout.admin_layout.nav_groups.dashboard'),
        path: '/admin',
        end: true,
        icon: <IcoDashboard />,
        ownerOnly: true,
      },
    ],
  },
  {
    group: 'Provoz',
    items: [
      {
        label: translate('components.layout.admin_layout.nav_groups.gym'),
        path: '/admin/gyms',
        icon: <IcoBuilding />,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.booking'),
        path: '/admin/bookings',
        icon: <IcoCalendar />,
        operatorAllowed: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.label'),
        path: '/admin/users',
        icon: <IcoUsers />,
        operatorAllowed: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.slot_pricing'),
        path: '/admin/slots',
        icon: <IcoBolt />,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.credit_edit'),
        path: '/admin/credits',
        icon: <IcoCredits />,
        platformOnly: true,
      },
    ],
  },
  {
    group: 'Platforma',
    items: [
      {
        label: translate('components.layout.admin_layout.nav_groups.tenant'),
        path: '/admin/franchise',
        icon: <IcoFranchise />,
        platformOnly: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.membership'),
        path: '/admin/memberships',
        icon: <IcoGift />,
        platformOnly: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.promo_code'),
        path: '/admin/promos',
        icon: <IcoGift />,
        platformOnly: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.referral'),
        path: '/admin/referrals',
        icon: <IcoGift />,
        platformOnly: true,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.email'),
        path: '/admin/emails',
        icon: <IcoMail />,
      },
      {
        label: translate('components.layout.admin_layout.nav_groups.settings'),
        path: '/admin/settings',
        icon: <IcoGear />,
        operatorAllowed: true,
      },
    ],
  },
];

export function AdminChromeProvider() {
  const [chrome, setChrome] = useState<AdminChromeState>({
    title: translate('components.layout.admin_layout.admin_chrome_provider.admin'),
    eyebrow: translate('components.layout.admin_layout.admin_chrome_provider.eyebrow'),
  });
  const [actionsTarget, setActionsTarget] = useState<HTMLDivElement | null>(null);
  const contextValue = useMemo(() => ({ setChrome, actionsTarget }), [actionsTarget]);

  return (
    <AdminChromeContext.Provider value={contextValue}>
      <AdminShell title={chrome.title} eyebrow={chrome.eyebrow} actionsRef={setActionsTarget}>
        <Outlet />
      </AdminShell>
    </AdminChromeContext.Provider>
  );
}

export function AdminLayout({ children, title, eyebrow, headerActions }: AdminLayoutProps) {
  const chrome = useContext(AdminChromeContext);

  useLayoutEffect(() => {
    if (!chrome) return;

    chrome.setChrome((current) => {
      if (current.title === title && current.eyebrow === eyebrow) return current;
      return { title, eyebrow };
    });
  }, [chrome, eyebrow, title]);

  if (chrome) {
    return (
      <>
        {chrome.actionsTarget && headerActions && createPortal(headerActions, chrome.actionsTarget)}
        {children}
      </>
    );
  }

  return (
    <AdminShell title={title} eyebrow={eyebrow} headerActions={headerActions}>
      {children}
    </AdminShell>
  );
}

function AdminShell({ children, title, eyebrow, headerActions, actionsRef }: AdminLayoutProps & {
  actionsRef?: React.Ref<HTMLDivElement>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { availableTenants, tenantSlug, switchTenant, account, role, signOut, setTenantSlug } =
    useAuth();
  const isPlatform = role === 'PLATFORM_ADMIN';
  const isOperator = role === 'GYM_STAFF';
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileSidebarClosing, setIsMobileSidebarClosing] = useState(false);
  const [isTenantMenuOpen, setIsTenantMenuOpen] = useState(false);
  const [platformTenants, setPlatformTenants] = useState<Array<{ name: string; slug: string }>>([]);
  const tenantMenuRef = useRef<HTMLDivElement | null>(null);
  const mobileSidebarCloseTimerRef = useRef<number | null>(null);

  useEffect(() => {
    document.documentElement.classList.add('af-admin-root');
    document.body.classList.add('af-admin-body');
    return () => {
      document.documentElement.classList.remove('af-admin-root');
      document.body.classList.remove('af-admin-body');
      if (mobileSidebarCloseTimerRef.current) {
        window.clearTimeout(mobileSidebarCloseTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    closeMobileSidebar();
  }, [location.pathname]);

  useEffect(() => {
    if (!isTenantMenuOpen) return;

    function handlePointerDown(event: PointerEvent) {
      if (!tenantMenuRef.current?.contains(event.target as Node)) {
        setIsTenantMenuOpen(false);
      }
    }

    window.addEventListener('pointerdown', handlePointerDown);
    return () => window.removeEventListener('pointerdown', handlePointerDown);
  }, [isTenantMenuOpen]);

  useEffect(() => {
    if (!isPlatform) {
      setPlatformTenants([]);
      return;
    }

    let cancelled = false;
    async function loadPlatformTenants() {
      const firstPage = await adminApi.listTenants(1, 100);
      const remainingPages = Array.from(
        { length: Math.max(0, firstPage.data.meta.totalPages - 1) },
        (_, index) => index + 2,
      );
      const remaining = await Promise.all(
        remainingPages.map((page) => adminApi.listTenants(page, 100)),
      );
      if (cancelled) return;

      setPlatformTenants(
        [firstPage, ...remaining]
          .flatMap((response) => response.data.data)
          .filter((tenant) => tenant.isActive)
          .map((tenant) => ({ name: tenant.name, slug: tenant.slug })),
      );
    }

    void loadPlatformTenants();
    return () => {
      cancelled = true;
    };
  }, [isPlatform]);

  async function handleTenantChange(nextTenantSlug: string) {
    if (!nextTenantSlug || nextTenantSlug === tenantSlug) return;
    if (isPlatform && !availableTenants.some((item) => item.tenant.slug === nextTenantSlug)) {
      setTenantSlug(nextTenantSlug);
      setIsTenantMenuOpen(false);
      return;
    }
    await switchTenant(nextTenantSlug);
    setIsTenantMenuOpen(false);
  }

  async function handleSignOut() {
    await signOut();
    void navigate('/', { replace: true });
  }

  function openMobileSidebar() {
    if (mobileSidebarCloseTimerRef.current) {
      window.clearTimeout(mobileSidebarCloseTimerRef.current);
      mobileSidebarCloseTimerRef.current = null;
    }

    setIsMobileSidebarClosing(false);
    setIsMobileSidebarOpen(true);
  }

  function closeMobileSidebar() {
    if (!isMobileSidebarOpen || isMobileSidebarClosing) return;

    setIsMobileSidebarClosing(true);
    mobileSidebarCloseTimerRef.current = window.setTimeout(() => {
      setIsMobileSidebarOpen(false);
      setIsMobileSidebarClosing(false);
      mobileSidebarCloseTimerRef.current = null;
    }, 260);
  }

  function toggleMobileSidebar() {
    if (isMobileSidebarOpen && !isMobileSidebarClosing) {
      closeMobileSidebar();
      return;
    }

    openMobileSidebar();
  }

  const tenantOptions =
    isPlatform && platformTenants.length > 0
      ? platformTenants
      : availableTenants.map((item) => ({ name: item.tenant.name, slug: item.tenant.slug }));
  const tenantLabel =
    tenantOptions.find((tenant) => tenant.slug === tenantSlug)?.name ??
    availableTenants.find((item) => item.tenant.slug === tenantSlug)?.tenant.name ??
    tenantSlug ??
    translate('components.layout.admin_layout.admin_shell.default_tenant_name');

  const canOpenTenantMenu = tenantOptions.length > 1 || isPlatform;

  return (
    <main className="af-screen af-admin-screen">
      <div className="af-admin-shell">
        <button
          type="button"
          className={`af-admin-sidebar-backdrop${isMobileSidebarOpen ? ' is-open' : ''}${isMobileSidebarClosing ? ' is-closing' : ''}`}
          aria-label={translate(
            'components.layout.admin_layout.admin_shell.button.aria_label_close',
          )}
          onClick={closeMobileSidebar}
        />

        <aside
          className={`af-admin-sidebar${isMobileSidebarOpen ? ' is-open' : ''}${isMobileSidebarClosing ? ' is-closing' : ''}`}
        >
          <div className="af-admin-sidebar-header">
            <RouterLink className="af-logo" to="/" style={{ marginBottom: 0 }}>
              <AtomFitLogo />
            </RouterLink>
            <LanguageSwitcher />
          </div>

          <div
            id="af-admin-sidebar-panel"
            className={`af-admin-sidebar-panel${isMobileSidebarOpen ? ' is-open' : ''}${isMobileSidebarClosing ? ' is-closing' : ''}`}
          >
            <div className="af-admin-tenant-switcher">
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--af-dim)',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: 8,
                }}
              >
                {translate('components.layout.admin_layout.admin_shell.div.tenant')}
              </div>
              {canOpenTenantMenu ? (
                <div className="af-admin-tenant-menu" ref={tenantMenuRef}>
                  <button
                    type="button"
                    className="af-admin-tenant-trigger"
                    aria-haspopup="listbox"
                    aria-expanded={isTenantMenuOpen}
                    onClick={() => setIsTenantMenuOpen((current) => !current)}
                  >
                    <span className="af-admin-tenant-trigger-text">{tenantLabel}</span>
                    <IcoChevronDown />
                  </button>
                  {isTenantMenuOpen && (
                    <div
                      className="af-admin-tenant-options"
                      role="listbox"
                      aria-label={translate(
                        'components.layout.admin_layout.admin_shell.div_admin_tenant_options.aria_label_tenant',
                      )}
                    >
                      {tenantOptions.map((tenant) => {
                        const isSelected = tenant.slug === tenantSlug;
                        return (
                          <button
                            key={tenant.slug}
                            type="button"
                            className={`af-admin-tenant-option${isSelected ? ' is-selected' : ''}`}
                            role="option"
                            aria-selected={isSelected}
                            onClick={() => void handleTenantChange(tenant.slug)}
                          >
                            <span className="af-admin-tenant-option-name">{tenant.name}</span>
                            {isSelected && <IcoCheck />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <div className="af-admin-tenant-static">{tenantLabel}</div>
              )}
            </div>

            <div
              style={{ width: '100%', height: 1, background: 'var(--af-border)', margin: '12px 0' }}
            />

            <nav style={{ flex: 1, padding: '0 10px' }}>
              {navGroups.map((group) => {
                const items = group.items.filter((item) => {
                  if (item.platformOnly && !isPlatform) return false;
                  if (item.ownerOnly && isOperator) return false;
                  if (isOperator && !item.operatorAllowed) return false;
                  return true;
                });
                if (items.length === 0) return null;

                return (
                  <div key={group.group ?? 'top'} style={{ marginBottom: 20 }}>
                    {group.group && (
                      <div
                        style={{
                          fontSize: 10,
                          color: 'var(--af-dim)',
                          fontWeight: 800,
                          textTransform: 'uppercase',
                          letterSpacing: '0.6px',
                          padding: '0 8px',
                          marginBottom: 4,
                        }}
                      >
                        {group.group}
                      </div>
                    )}
                    {items.map((item) => (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        end={Boolean(item.end)}
                        className={({ isActive }) =>
                          `af-admin-nav-item${isActive ? ' is-active' : ''}`
                        }
                      >
                        <span style={{ opacity: 0.75 }}>{item.icon}</span>
                        {item.label}
                        {item.badge && <span className="af-admin-nav-badge">{item.badge}</span>}
                      </NavLink>
                    ))}
                  </div>
                );
              })}
            </nav>

            <div style={{ padding: '12px 12px 16px', borderTop: '1px solid var(--af-border)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'var(--af-s3)',
                    border: '1px solid var(--af-border-strong)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 800,
                    color: 'var(--af-muted)',
                    flexShrink: 0,
                  }}
                >
                  {isPlatform ? '*' : (account?.email?.[0] ?? 'A').toUpperCase()}
                </div>
                <div style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: 'var(--af-text)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {isPlatform
                      ? translate('components.layout.admin_layout.admin_shell.div.admin')
                      : isOperator
                        ? translate('components.layout.admin_layout.admin_shell.div.copy')
                        : (account?.email ??
                          translate('components.layout.admin_layout.admin_shell.div.copy_2'))}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: 'var(--af-dim)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {account?.email ??
                      translate('components.layout.admin_layout.admin_shell.div.admin_2')}
                  </div>
                </div>
              </div>
              <button className="af-admin-signout" onClick={() => void handleSignOut()}>
                <IcoSignOut />
                {translate(
                  'components.layout.admin_layout.admin_shell.button_admin_signout.logout',
                )}
              </button>
            </div>
          </div>
        </aside>

        <section className="af-admin-content">
          <div className="af-admin-mobile-bar">
            <button
              type="button"
              className="af-admin-sidebar-toggle"
              aria-expanded={isMobileSidebarOpen && !isMobileSidebarClosing}
              aria-controls="af-admin-sidebar-panel"
              aria-label={
                isMobileSidebarOpen && !isMobileSidebarClosing
                  ? translate(
                      'components.layout.admin_layout.admin_shell.button_admin_sidebar_toggle.aria_label_content',
                    )
                  : translate(
                      'components.layout.admin_layout.admin_shell.button_admin_sidebar_toggle.aria_label_content_2',
                    )
              }
              onClick={toggleMobileSidebar}
            >
              <IcoMenu />
              {translate(
                'components.layout.admin_layout.admin_shell.button_admin_sidebar_toggle.menu',
              )}
            </button>
          </div>
          <div style={{ marginBottom: 28 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--af-dim)',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                marginBottom: 6,
              }}
            >
              {eyebrow}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', margin: 0 }}>
                {title}
              </h1>
              {isPlatform && eyebrow === 'Platform Admin' && (
                <Tag>{translate('components.layout.admin_layout.admin_shell.tag.copy')}</Tag>
              )}
              <div ref={actionsRef} style={{ marginLeft: 'auto', flexShrink: 0 }}>
                {headerActions}
              </div>
            </div>
          </div>
          {children}
        </section>
      </div>
    </main>
  );
}

function IcoDashboard() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="1" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="1" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
      <rect x="9" y="9" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function IcoChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M4 6l4 4 4-4"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoCheck() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.2l2.8 2.8 6.2-6.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoBuilding() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.4" />
      <path d="M6 14v-4h4v4" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M2 7h12" stroke="currentColor" strokeWidth="1.1" opacity="0.5" />
      <rect x="5" y="5.5" width="2" height="2" rx="0.4" fill="currentColor" opacity="0.5" />
      <rect x="9" y="5.5" width="2" height="2" rx="0.4" fill="currentColor" opacity="0.5" />
    </svg>
  );
}

function IcoCalendar() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1.5" y="3" width="13" height="11" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M1.5 7h13" stroke="currentColor" strokeWidth="1.2" />
      <path d="M5 1.5v3M11 1.5v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.5" cy="10" r="1" fill="currentColor" />
      <circle cx="8" cy="10" r="1" fill="currentColor" />
      <circle cx="10.5" cy="10" r="1" fill="currentColor" />
    </svg>
  );
}

function IcoUsers() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M1.8 14c.5-2.6 2-4 4.2-4s3.7 1.4 4.2 4"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />

      <path
        d="M10.5 7.5a2.1 2.1 0 1 0 0-4.2M11.3 10.2c1.4.4 2.4 1.6 2.8 3.8"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.65"
      />
    </svg>
  );
}

function IcoBolt() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M9.5 1.5L4 9h5.5L6.5 14.5 13 7H7.5l2-5.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoCredits() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3.5" width="12" height="9" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 7h7M4.5 9.5h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
      <path
        d="M11 1.8v3.4"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        opacity="0.7"
      />
    </svg>
  );
}

function IcoGift() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2.5" y="7" width="11" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 7h12V4.5H2V7zM8 4.5V14" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 4.5C6.4 4.5 4.6 4.2 4.6 3c0-.8.6-1.4 1.4-1.4 1.1 0 1.8 1.4 2 2.9zM8 4.5c1.6 0 3.4-.3 3.4-1.5 0-.8-.6-1.4-1.4-1.4-1.1 0-1.8 1.4-2 2.9z"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoFranchise() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="3" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="2.5" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="13.5" cy="3.5" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <circle cx="8" cy="14" r="1.5" stroke="currentColor" strokeWidth="1.2" />
      <path
        d="M4 5l2.5 2M12 5l-2.5 2M8 11v1.5"
        stroke="currentColor"
        strokeWidth="1.1"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IcoGear() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IcoMail() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3.5" width="12" height="9" rx="1.6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M3 5l5 3.7L13 5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IcoSignOut() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
      <path
        d="M5 7.5h8M10 5l3 2.5L10 10"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M8 3H3a1 1 0 0 0-1 1v7a1 1 0 0 0 1 1h5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IcoMenu() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
      <path
        d="M3 5h12M3 9h12M3 13h12"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </svg>
  );
}
